import { tt } from '@agos/shared'

/**
 * `/settings-general` — one screen over three settings sections.
 *
 * **FB-131.** Three flags are stored twice, under two spellings, and only one copy is read by the
 * thing that matters:
 *
 * | Flag | `/settings/general` | `/settings/system` |
 * |---|---|---|
 * | maintenance mode | `maintenanceMode` **(column)** | `maintenanceMode` (blob) |
 * | online orders | `onlineOrdersEnabled` **(column)** | `onlineOrdersEnabled` (blob) |
 * | auto-accept | `autoAccept` **(column)** | `autoAcceptOrders` (blob) |
 *
 * `RestaurantAvailability` — the code that decides whether an order may be placed at all — reads the
 * **columns**:
 *
 * ```java
 * "SELECT maintenance_mode, online_orders_enabled, auto_accept" …
 * if (switches.get().maintenanceMode()) { … }
 * ```
 *
 * Nothing reads the blob copy except this page and the Control Tower's per-franchise view. Legacy writes
 * only the columns, so its own toggles work — but the blob is never updated, so a super admin looking at
 * the franchise sees whatever was last written there, which may be months stale. `kitchenDisplayInterval`
 * is duplicated the same way between `/settings/general` and `/settings/basic`, and legacy writes only
 * the `basic` copy.
 *
 * This page reads the authoritative copy, writes the authoritative copy, and says when the two disagree
 * rather than silently showing one of them.
 *
 * **FB-132.** `GET /settings/basic` answers sixteen fields, one of which is a live Anthropic API key,
 * and the endpoint admits any `admin`. This page needs four of the sixteen. Nothing here renders, logs
 * or echoes the key — and the payload shape is filtered on arrival so it cannot reach a form value by
 * accident. Filed as a backend item: a screen that wants four unrelated fields should not be handed a
 * credential to get them.
 */

/**
 * The twelve fields this page used to store that **nothing reads** — §9.285, owner O25, FB-435.
 *
 * §9.277 measured them and labelled them on screen; the owner asked for them to go. Measured on three
 * sides before either step: absent from every identifier in `backend/**\/src/main` (16 909 of them),
 * absent from the rest of the frontend, and present in legacy in exactly one file —
 * `settings-general.tsx`, the page that writes them. So legacy's server ignores them too.
 *
 * They split two ways, and the split is the owner's: four shadow a screen that works and are simply
 * gone, eight are not built yet and are listed in *Pripravujeme* so their removal does not read as a
 * feature being taken away.
 */

export interface ComingSoon {
  /** The key that used to be sent, so the list can be checked against what was removed. */
  key: string
  label: string
  description: string
}

/** The eight the owner asked to be listed rather than switched. */
export const COMING_SOON: readonly ComingSoon[] = [
  {
    key: 'guestCheckout',
    label: tt('Objednávka bez registrácie'),
    // Said as it is: the checkout has always had a guest path, so this switch could only ever have
    // promised to take something away that in fact it could not.
    description: tt('Objednať sa dá aj bez konta už teraz - prepínač na to zatiaľ nemá vplyv.'),
  },
  {
    key: 'requirePhoneVerification',
    label: tt('Overenie telefónu'),
    description: tt('Zákazník potvrdí číslo kódom. Zatiaľ nie je naprogramované.'),
  },
  {
    key: 'cookieBanner',
    label: tt('Cookie lišta'),
    description: tt('Súhlas pred vložením analytických skriptov. Zatiaľ nie je naprogramované.'),
  },
  {
    key: 'showAllergens',
    label: tt('Zobrazovať alergény'),
    description: tt('Alergény sa v menu zobrazujú vždy - vypnúť sa zatiaľ nedajú.'),
  },
  {
    key: 'showCalories',
    label: tt('Zobrazovať kalórie'),
    description: tt('Kalórie sa pri produktoch zatiaľ nezobrazujú.'),
  },
  {
    key: 'analyticsEnabled',
    label: tt('Analytika zákazníckej stránky'),
    description: tt('Meranie návštevnosti. Zatiaľ nie je naprogramované.'),
  },
  {
    key: 'maxOrdersPerHour',
    label: tt('Maximálne objednávok za hodinu'),
    description: tt('Obmedzenie počtu objednávok. Zatiaľ nie je naprogramované.'),
  },
  {
    key: 'orderPrefix',
    label: tt('Predvoľba čísla objednávky'),
    description: tt('Čísla objednávok sa zatiaľ generujú bez predvoľby.'),
  },
]

export interface MovedElsewhere {
  key: string
  label: string
  /** The page whose switch actually does this. */
  where: string
}

/**
 * The four that were removed because the real switch is on another page.
 *
 * These are the dangerous ones, and why they are named rather than deleted in silence: an owner who
 * switched notifications off here would have found them still going, because the switch that counts
 * is under **Systémové nastavenia → Upozornenia**.
 */
export const MOVED_ELSEWHERE: readonly MovedElsewhere[] = [
  {
    key: 'pushNotifications',
    label: tt('Push notifikácie'),
    where: tt('Systémové nastavenia → Upozornenia'),
  },
  {
    key: 'emailNotifications',
    label: tt('E-mailové upozornenia'),
    where: tt('Systémové nastavenia → Upozornenia'),
  },
  {
    key: 'smsNotifications',
    label: tt('SMS upozornenia'),
    where: tt('Systémové nastavenia → Upozornenia'),
  },
  {
    key: 'autoBackup',
    label: tt('Automatické zálohovanie'),
    where: tt('Systémové nastavenia → Zálohy'),
  },
]

/** Everything that left the page, for the test that asserts nothing still sends it. */
export const REMOVED_FIELDS: readonly string[] = [
  ...COMING_SOON.map((entry) => entry.key),
  ...MOVED_ELSEWHERE.map((entry) => entry.key),
]

// ─── the three sections, as the endpoints answer them ────────────────────────

export interface GeneralSection {
  maintenanceMode?: boolean
  onlineOrdersEnabled?: boolean
  autoAccept?: boolean
  phoneNumber?: string
  announcement?: string
  announcementEnabled?: boolean
  footerAddress?: string
  kitchenDisplayInterval?: number
}

export interface SystemSection {
  maintenanceMode?: boolean
  onlineOrdersEnabled?: boolean
  autoAcceptOrders?: boolean
  guestCheckout?: boolean
  requireAccount?: boolean
  cookieBanner?: boolean
  showAllergens?: boolean
  showCalories?: boolean
  requirePhoneVerification?: boolean
  pushNotifications?: boolean
  emailNotifications?: boolean
  smsNotifications?: boolean
  analyticsEnabled?: boolean
  autoBackup?: boolean
  timezone?: string
  language?: string
  orderPrefix?: string
  maxOrdersPerHour?: number
  currencySymbol?: string
}

/**
 * The four fields this page wants out of `/settings/basic`, and nothing else.
 *
 * FB-132: the section also holds `googleAnalyticsId`, `metaPixelId`, `tiktokPixelId`, the delivery
 * pricing and the translation settings. Naming the four explicitly means nothing else enters this
 * page's state, so it cannot end up in a form value, a log line or a React devtools tree.
 *
 * **The endpoint no longer answers `anthropicApiKey` at all** (`MIGRATION-STATUS`, 2026-08-02) — it
 * sends `anthropicApiKeySet`, a boolean, which was the only thing any screen wanted from it. This list
 * predates that and stays: a page taking what it needs by name is right regardless of what the
 * endpoint happens to include today.
 */
export const BASIC_FIELDS_USED: readonly string[] = [
  'customHeaderCode',
  'customFooterCode',
  'highlightedPhones',
  'kitchenDisplayInterval',
]

export interface BasicSlice {
  customHeaderCode: string
  customFooterCode: string
  highlightedPhones: string
  kitchenDisplayInterval: number
}

export function basicSlice(section: Record<string, unknown> | undefined): BasicSlice {
  const text = (key: string): string => {
    const value = section?.[key]
    return typeof value === 'string' ? value : ''
  }
  const count = section?.kitchenDisplayInterval
  return {
    customHeaderCode: text('customHeaderCode'),
    customFooterCode: text('customFooterCode'),
    highlightedPhones: text('highlightedPhones'),
    kitchenDisplayInterval: typeof count === 'number' ? count : DEFAULT_KITCHEN_INTERVAL,
  }
}

/**
 * Whether a payload from `/settings/basic` carries something this page must not touch.
 *
 * The server stopped sending the credential in 2026-08-02, so this should now always be false — which
 * is exactly why it is kept. It is the assertion that the narrowing held, and a test pins it: if the
 * key ever reappears in that payload, this says so rather than the page quietly carrying it again.
 */
export function carriesCredential(section: Record<string, unknown> | undefined): boolean {
  const key = section?.anthropicApiKey
  return typeof key === 'string' && key.trim() !== ''
}

// ─── the form ────────────────────────────────────────────────────────────────

export const DEFAULT_KITCHEN_INTERVAL = 60
export const KITCHEN_INTERVAL_MIN = 5
export const KITCHEN_INTERVAL_MAX = 600
export const PHONE_NUMBER_MAX = 32

export interface GeneralForm {
  // The authoritative switches — columns on `restaurant_settings`.
  maintenanceMode: boolean
  onlineOrdersEnabled: boolean
  autoAccept: boolean
  phoneNumber: string
  announcement: string
  footerAddress: string
  // `system_settings_json`.
  timezone: string
  language: string
  // `basic_settings`.
  customHeaderCode: string
  customFooterCode: string
  highlightedPhones: string
  kitchenDisplayInterval: number
}

export const EMPTY_FORM: GeneralForm = {
  maintenanceMode: false,
  onlineOrdersEnabled: true,
  autoAccept: false,
  phoneNumber: '',
  announcement: '',
  footerAddress: '',
  timezone: 'Europe/Bratislava',
  language: 'sk',
  customHeaderCode: '',
  customFooterCode: '',
  highlightedPhones: '',
  kitchenDisplayInterval: DEFAULT_KITCHEN_INTERVAL,
}

export function formOf(
  general: GeneralSection | undefined,
  system: SystemSection | undefined,
  basic: Record<string, unknown> | undefined,
): GeneralForm {
  const slice = basicSlice(basic)
  return {
    // The columns, not the blob — these three are what the order gate reads.
    maintenanceMode: general?.maintenanceMode ?? EMPTY_FORM.maintenanceMode,
    onlineOrdersEnabled: general?.onlineOrdersEnabled ?? EMPTY_FORM.onlineOrdersEnabled,
    autoAccept: general?.autoAccept ?? EMPTY_FORM.autoAccept,
    phoneNumber: general?.phoneNumber ?? '',
    announcement: general?.announcement ?? '',
    footerAddress: general?.footerAddress ?? '',
    timezone: system?.timezone ?? EMPTY_FORM.timezone,
    language: system?.language ?? EMPTY_FORM.language,
    customHeaderCode: slice.customHeaderCode,
    customFooterCode: slice.customFooterCode,
    highlightedPhones: slice.highlightedPhones,
    kitchenDisplayInterval: slice.kitchenDisplayInterval,
  }
}

// ─── FB-131: the copies that disagree ────────────────────────────────────────

export type SharedFlag = 'maintenanceMode' | 'onlineOrdersEnabled' | 'autoAccept'

export interface FlagCopies {
  flag: SharedFlag
  /** The column, which `RestaurantAvailability` reads. */
  authoritative: boolean
  /** The `system_settings_json` copy, which the Control Tower reads. */
  mirror: boolean
}

export function flagCopies(
  general: GeneralSection | undefined,
  system: SystemSection | undefined,
): FlagCopies[] {
  return [
    {
      flag: 'maintenanceMode' as const,
      authoritative: general?.maintenanceMode === true,
      mirror: system?.maintenanceMode === true,
    },
    {
      flag: 'onlineOrdersEnabled' as const,
      // The column defaults to open; an absent value must not read as closed.
      authoritative: general?.onlineOrdersEnabled !== false,
      mirror: system?.onlineOrdersEnabled !== false,
    },
    {
      flag: 'autoAccept' as const,
      authoritative: general?.autoAccept === true,
      // Note the different key. The same setting, spelled two ways.
      mirror: system?.autoAcceptOrders === true,
    },
  ]
}

export function staleFlags(
  general: GeneralSection | undefined,
  system: SystemSection | undefined,
): FlagCopies[] {
  return flagCopies(general, system).filter((copy) => copy.authoritative !== copy.mirror)
}

export function flagLabel(flag: SharedFlag): string {
  if (flag === 'maintenanceMode') return tt('Režim údržby')
  if (flag === 'onlineOrdersEnabled') return tt('Online objednávky')
  return tt('Automatické potvrdzovanie')
}

/** Whether `/settings/general`'s copy of the kitchen interval has drifted from `/settings/basic`'s. */
export function kitchenIntervalDrift(
  general: GeneralSection | undefined,
  basic: Record<string, unknown> | undefined,
): { general: number; basic: number } | null {
  const fromGeneral = general?.kitchenDisplayInterval
  const fromBasic = basicSlice(basic).kitchenDisplayInterval
  if (typeof fromGeneral !== 'number' || fromGeneral === fromBasic) return null
  return { general: fromGeneral, basic: fromBasic }
}

// ─── what gets written, and where ────────────────────────────────────────────

export interface GeneralPayload {
  maintenanceMode: boolean
  onlineOrdersEnabled: boolean
  autoAccept: boolean
  phoneNumber: string
  announcement: string
  announcementEnabled: boolean
  footerAddress: string
}

/**
 * `PUT /settings/general` writes only the columns it is given a key for, so a partial body is safe.
 * The full one is sent anyway: a save button that stores some of the form and not the rest is the
 * thing FB-130's single success message hid.
 */
export function generalPayload(form: GeneralForm): GeneralPayload {
  return {
    maintenanceMode: form.maintenanceMode,
    onlineOrdersEnabled: form.onlineOrdersEnabled,
    autoAccept: form.autoAccept,
    phoneNumber: form.phoneNumber.trim(),
    announcement: form.announcement.trim(),
    // Legacy's own rule: an announcement with text in it is on.
    announcementEnabled: form.announcement.trim() !== '',
    footerAddress: form.footerAddress.trim(),
  }
}

export interface SystemPayload {
  timezone: string
  language: string
  /** FB-131: the mirror is written too, so the Control Tower stops showing a stale flag. */
  maintenanceMode: boolean
  onlineOrdersEnabled: boolean
  autoAcceptOrders: boolean
}

export function systemPayload(form: GeneralForm): SystemPayload {
  return {
    timezone: form.timezone.trim(),
    language: form.language,
    maintenanceMode: form.maintenanceMode,
    onlineOrdersEnabled: form.onlineOrdersEnabled,
    autoAcceptOrders: form.autoAccept,
  }
}

export interface BasicPayload {
  /** Sent only by whoever may change it - `MAIN_ADMIN_BASIC_KEYS`. */
  customHeaderCode?: string
  customFooterCode?: string
  highlightedPhones: string
  kitchenDisplayInterval: number
}

/**
 * The two keys of `/settings/basic` this page shows that only the main administrator may change - FRONTEND.md
 * §9.758, BACKEND.md §10A.413.
 *
 * `PUT /settings/basic` asks by key now: `highlightedPhones` and `kitchenDisplayInterval` are `settings_general`'s,
 * and a change of any key it does not name - these two among them - stays the main administrator's, because the code
 * is put into the customer site's HTML and runs before every customer.
 */
export const MAIN_ADMIN_BASIC_KEYS = ['customHeaderCode', 'customFooterCode'] as const

/**
 * `PUT /settings/basic` is a `mergeSection`, so sending four keys leaves the other twelve - including
 * the credential - untouched. Sending the whole section back would mean holding it, which is the thing
 * FB-132 says not to do.
 *
 * **`mayWriteCode` is required** (§9.480): without it the custom code is **left out** rather than sent unchanged.
 * The server decides by what changes against the stored row, so an unchanged value would pass - until somebody else
 * edited the code between the read and the save, and then a person who never touched it would be refused a whole
 * save. A key not sent is a key the merge leaves alone, which is the answer that cannot be refused.
 */
export function basicPayload(form: GeneralForm, mayWriteCode: boolean): BasicPayload {
  return {
    ...(mayWriteCode
      ? { customHeaderCode: form.customHeaderCode, customFooterCode: form.customFooterCode }
      : {}),
    highlightedPhones: form.highlightedPhones.trim(),
    kitchenDisplayInterval: clampInterval(form.kitchenDisplayInterval),
  }
}

/** Said over the custom code to somebody who may store this page and not that - §9.758. */
export function customCodeNote(): string {
  return tt('Vlastný kód beží na zákazníckej stránke, preto ho mení len hlavný administrátor. Ostatné nastavenia uložíte.')
}

export function clampInterval(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_KITCHEN_INTERVAL
  return Math.min(KITCHEN_INTERVAL_MAX, Math.max(KITCHEN_INTERVAL_MIN, Math.round(value)))
}

// ─── what the switches mean ──────────────────────────────────────────────────

export type Severity = 'closes-shop' | 'ordinary'

/**
 * Which switches stop the restaurant taking money.
 *
 * `RestaurantAvailability` refuses an order outright when maintenance mode is on or online orders are
 * off. Legacy renders all twenty-three switches identically, so the two that close the shop look like
 * the one that shows calories.
 */
export function severityOf(field: keyof GeneralForm): Severity {
  return field === 'maintenanceMode' || field === 'onlineOrdersEnabled' ? 'closes-shop' : 'ordinary'
}

export interface ShopState {
  takingOrders: boolean
  reason: 'open' | 'maintenance' | 'orders-off'
}

export function shopState(form: GeneralForm): ShopState {
  if (form.maintenanceMode) return { takingOrders: false, reason: 'maintenance' }
  if (!form.onlineOrdersEnabled) return { takingOrders: false, reason: 'orders-off' }
  return { takingOrders: true, reason: 'open' }
}

export function shopStateMessage(state: ShopState): string {
  if (state.reason === 'maintenance') {
    return tt('Režim údržby je zapnutý - zákazníci si teraz nemôžu nič objednať.')
  }
  if (state.reason === 'orders-off') {
    return tt('Online objednávky sú vypnuté - zákazníci si teraz nemôžu nič objednať.')
  }
  return tt('Objednávky sú otvorené.')
}

/** A change that closes the shop, so it can be confirmed rather than toggled past. */
export function closesShop(before: GeneralForm, after: GeneralForm): boolean {
  return shopState(before).takingOrders && !shopState(after).takingOrders
}

export const TIMEZONES: readonly string[] = [
  'Europe/Bratislava',
  'Europe/Prague',
  'Europe/Vienna',
  'Europe/Budapest',
  'Europe/Warsaw',
  'UTC',
]

/**
 * A language names itself in its own language, so these are endonyms and not translations.
 *
 * `Česky` and `Slovensky` are what a Czech and a Slovak speaker expect to see in a language menu; a
 * *translated* language name is the thing that reads wrong (nobody wants to pick `Slovak` on a Slovak
 * screen). So the labels deliberately do not go through `tt`.
 */
/* eslint-disable no-restricted-syntax -- endonyms: each label is in the language it names */
export const LANGUAGES: readonly { code: string; label: string }[] = [
  { code: 'sk', label: 'Slovensky' },
  { code: 'cs', label: 'Česky' },
  { code: 'en', label: 'English' },
  { code: 'hu', label: 'Magyar' },
  { code: 'de', label: 'Deutsch' },
  { code: 'it', label: 'Italiano' },
]
/* eslint-enable no-restricted-syntax */

/** The custom head and foot code go into the customer site's HTML. */
export function injectsScript(code: string): boolean {
  return code.toLowerCase().includes('<script')
}
